import { useEffect, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Save, Plus, Trash2, ChevronUp, ChevronDown, GripVertical,
  Layers, Box, Sparkles, Building2, BookOpen, Briefcase,
  Users, Grid3x3, Home, Package, Zap, ShoppingCart, Award,
  Star, Newspaper, GraduationCap, FileText, Mail, Phone, Info,
  Menu as MenuIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated icon set the admin can pick from. Icons are mapped to Lucide
// components in Header.jsx at render time.
const ICON_OPTIONS = [
  { value: "layers",       label: "Layers",       Icon: Layers },
  { value: "box",          label: "Box",          Icon: Box },
  { value: "sparkles",     label: "Sparkles",     Icon: Sparkles },
  { value: "building-2",   label: "Building",     Icon: Building2 },
  { value: "book-open",    label: "Book",         Icon: BookOpen },
  { value: "briefcase",    label: "Briefcase",    Icon: Briefcase },
  { value: "users",        label: "Users",        Icon: Users },
  { value: "grid-3x3",     label: "Grid",         Icon: Grid3x3 },
  { value: "home",         label: "Home",         Icon: Home },
  { value: "package",      label: "Package",      Icon: Package },
  { value: "zap",          label: "Zap",          Icon: Zap },
  { value: "shopping-cart",label: "Shopping",     Icon: ShoppingCart },
  { value: "award",        label: "Award",        Icon: Award },
  { value: "star",         label: "Star",         Icon: Star },
  { value: "newspaper",    label: "Newspaper",    Icon: Newspaper },
  { value: "graduation-cap",label: "Academy",     Icon: GraduationCap },
  { value: "file-text",    label: "Doc",          Icon: FileText },
  { value: "mail",         label: "Mail",         Icon: Mail },
  { value: "phone",        label: "Phone",        Icon: Phone },
  { value: "info",         label: "Info",         Icon: Info },
];

function slugId() {
  return `nav-${Math.random().toString(36).slice(2, 8)}`;
}

export default function AdminNavigationPage() {
  const [nav, setNav] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/site-settings`);
        setNav(data.navigation || []);
      } catch (e) {
        toast.error("Failed to load navigation");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/site-settings`, { navigation: nav });
      toast.success("Navigation saved. Changes are live on the site immediately.");
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // -- Top-level operations --
  const addTop = () => setNav([...nav, {
    id: slugId(), label: "New Item", path: "/", icon: "layers", items: [],
  }]);

  const removeTop = (i) => {
    if (!window.confirm(`Remove "${nav[i].label}" and its ${nav[i].items?.length || 0} sub-items?`)) return;
    setNav(nav.filter((_, idx) => idx !== i));
  };

  const moveTop = (i, dir) => {
    const next = [...nav];
    const target = i + dir;
    if (target < 0 || target >= next.length) return;
    [next[i], next[target]] = [next[target], next[i]];
    setNav(next);
  };

  const updateTop = (i, patch) => {
    const next = [...nav];
    next[i] = { ...next[i], ...patch };
    setNav(next);
  };

  // -- Sub-item operations --
  const addSub = (i) => {
    const next = [...nav];
    next[i] = { ...next[i], items: [...(next[i].items || []), { id: slugId(), label: "New Sub-item", path: "/" }] };
    setNav(next);
  };
  const removeSub = (i, j) => {
    const next = [...nav];
    next[i] = { ...next[i], items: next[i].items.filter((_, idx) => idx !== j) };
    setNav(next);
  };
  const moveSub = (i, j, dir) => {
    const next = [...nav];
    const arr = [...(next[i].items || [])];
    const target = j + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[j], arr[target]] = [arr[target], arr[j]];
    next[i] = { ...next[i], items: arr };
    setNav(next);
  };
  const updateSub = (i, j, patch) => {
    const next = [...nav];
    const arr = [...(next[i].items || [])];
    arr[j] = { ...arr[j], ...patch };
    next[i] = { ...next[i], items: arr };
    setNav(next);
  };

  if (loading) {
    return (
      <div className="p-8 text-slate-500">Loading navigation…</div>
    );
  }

  return (
    <div data-testid="admin-navigation-page" className="min-h-screen">
      <Toaster position="top-right" richColors closeButton />
      <header className="bg-white border-b border-slate-200 px-5 lg:px-8 py-5 sticky top-0 z-10 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center">
            <MenuIcon className="h-4 w-4 text-sky-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Top Navigation</h1>
            <p className="text-xs text-slate-500">Add, remove, rename, reorder menu items. Changes go live instantly on save.</p>
          </div>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          data-testid="save-navigation"
          className="bg-sky-600 hover:bg-sky-700 text-white rounded-lg h-10 px-5 text-sm font-semibold"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </header>

      <div className="p-5 lg:p-8 max-w-5xl mx-auto space-y-4">
        <div className="bg-sky-50/50 border border-sky-100 rounded-lg p-4 text-xs text-slate-600 leading-relaxed">
          <strong className="text-slate-800">How this works:</strong> Each row below is a top-level menu button. If a row has one or more sub-items, it becomes a dropdown. To add a new page to the site,
          create the page first (see <span className="font-mono bg-white border border-slate-200 rounded px-1 py-0.5">Site Pages</span> or <span className="font-mono bg-white border border-slate-200 rounded px-1 py-0.5">Custom Pages</span>), then add a menu item here pointing to its URL.
        </div>

        {nav.map((item, i) => (
          <div
            key={item.id || i}
            data-testid={`nav-top-${i}`}
            className="bg-white border border-slate-200 rounded-xl overflow-hidden"
          >
            <div className="flex items-center gap-3 p-4 border-b border-slate-100 bg-slate-50/50">
              <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Label</Label>
                  <Input
                    value={item.label || ""}
                    onChange={(e) => updateTop(i, { label: e.target.value })}
                    data-testid={`nav-top-${i}-label`}
                    placeholder="Products"
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Link (URL path)</Label>
                  <Input
                    value={item.path || ""}
                    onChange={(e) => updateTop(i, { path: e.target.value })}
                    data-testid={`nav-top-${i}-path`}
                    placeholder="/products"
                    className="h-9 text-sm font-mono"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Icon</Label>
                  <select
                    value={item.icon || "layers"}
                    onChange={(e) => updateTop(i, { icon: e.target.value })}
                    data-testid={`nav-top-${i}-icon`}
                    className="w-full h-9 text-sm border border-slate-200 rounded-md px-2 bg-white"
                  >
                    {ICON_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => moveTop(i, -1)}
                  disabled={i === 0}
                  data-testid={`nav-top-${i}-up`}
                  className="p-1.5 rounded hover:bg-slate-200 disabled:opacity-30"
                  title="Move up"
                >
                  <ChevronUp className="h-4 w-4 text-slate-600" />
                </button>
                <button
                  type="button"
                  onClick={() => moveTop(i, 1)}
                  disabled={i === nav.length - 1}
                  data-testid={`nav-top-${i}-down`}
                  className="p-1.5 rounded hover:bg-slate-200 disabled:opacity-30"
                  title="Move down"
                >
                  <ChevronDown className="h-4 w-4 text-slate-600" />
                </button>
                <button
                  type="button"
                  onClick={() => removeTop(i)}
                  data-testid={`nav-top-${i}-remove`}
                  className="p-1.5 rounded hover:bg-red-50 text-red-500"
                  title="Remove this menu item"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Sub-items */}
            <div className="p-4 space-y-2">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Dropdown items{item.items?.length ? ` (${item.items.length})` : " — none, this renders as a single link"}
                </p>
                <button
                  type="button"
                  onClick={() => addSub(i)}
                  data-testid={`nav-top-${i}-add-sub`}
                  className="text-xs font-semibold text-sky-600 hover:text-sky-700 inline-flex items-center gap-1"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add sub-item
                </button>
              </div>
              {(item.items || []).map((sub, j) => (
                <div
                  key={sub.id || j}
                  data-testid={`nav-sub-${i}-${j}`}
                  className="flex items-center gap-2 pl-6 border-l-2 border-sky-100"
                >
                  <Input
                    value={sub.label || ""}
                    onChange={(e) => updateSub(i, j, { label: e.target.value })}
                    data-testid={`nav-sub-${i}-${j}-label`}
                    placeholder="Sub-item label"
                    className="h-9 text-sm flex-1"
                  />
                  <Input
                    value={sub.path || ""}
                    onChange={(e) => updateSub(i, j, { path: e.target.value })}
                    data-testid={`nav-sub-${i}-${j}-path`}
                    placeholder="/some/path"
                    className="h-9 text-sm font-mono flex-1"
                  />
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => moveSub(i, j, -1)}
                      disabled={j === 0}
                      data-testid={`nav-sub-${i}-${j}-up`}
                      className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                    >
                      <ChevronUp className="h-3.5 w-3.5 text-slate-500" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveSub(i, j, 1)}
                      disabled={j === (item.items || []).length - 1}
                      data-testid={`nav-sub-${i}-${j}-down`}
                      className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                    >
                      <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeSub(i, j)}
                      data-testid={`nav-sub-${i}-${j}-remove`}
                      className="p-1 rounded hover:bg-red-50 text-red-500"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={addTop}
          data-testid="add-top-nav-item"
          className="w-full py-4 border-2 border-dashed border-slate-300 rounded-xl text-sm font-semibold text-slate-500 hover:border-sky-400 hover:text-sky-600 hover:bg-sky-50/30 transition-colors inline-flex items-center justify-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add top-level menu item
        </button>
      </div>
    </div>
  );
}
